import { Component } from '@theme/component';
import { prefersReducedMotion, clamp } from '@theme/utilities';

/** How far the sky image pans, as a percentage of its own width. */
const MAX_SKY_SHIFT_PERCENT = 5;
/** How many pixels the jar rises for every pixel scrolled. */
const JAR_PARALLAX_SPEED = 0.18;
/** Hard cap, in pixels, on how far the jar can rise. */
const MAX_JAR_SHIFT_PX = 140;

/**
 * A custom element that pans a sky image left-to-right and floats a jar
 * image upward as the hero scrolls, for a subtle sense of depth/motion.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} sky
 * @property {HTMLImageElement} jar
 *
 * @extends Component<Refs>
 */
export class HeroParallaxMedia extends Component {
  requiredRefs = ['sky', 'jar'];

  /** @type {number | null} */
  #rafId = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** Cached position/size, refreshed on connect/resize rather than read on
   *  every scroll frame — reading getBoundingClientRect() per frame forces a
   *  synchronous layout recalculation, which was the main cause of the
   *  jagged/jaggy scroll animation on mobile. */
  #heroTop = 0;
  #heroHeight = 1;
  #resizeRafId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#measure();
    window.addEventListener('resize', this.#handleResize, { passive: true });

    // Trigger the entrance animation for every text and button block in the
    // hero, staggered in document order. This lives here (rather than an
    // inline <script> in the section) because custom elements reliably run
    // their lifecycle callbacks when Shopify's theme editor swaps section HTML
    // via AJAX, whereas plain inline <script> tags inserted that way do not.
    const contentWrapper = this.closest('.hero')?.querySelector('.hero__content-wrapper');
    if (contentWrapper) {
      const revealTargets = contentWrapper.querySelectorAll('.text-block, .button, .button-secondary, .link');
      revealTargets.forEach((el, i) => {
        el.classList.add('hero-reveal');
        el.style.setProperty('--reveal-index', i);
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          revealTargets.forEach((el) => el.classList.add('hero-reveal--in-view'));
        });
      });
    }

    if (prefersReducedMotion()) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, { rootMargin: '20% 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    this.#observer?.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#resizeRafId) cancelAnimationFrame(this.#resizeRafId);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    const isVisible = entries.some((entry) => entry.isIntersecting);

    if (isVisible) {
      // Re-measure now rather than trusting the connect-time value: layout
      // may have shifted (fonts loading, orientation change) while this was
      // still off-screen.
      this.#measure();
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.#handleScroll();
    } else {
      window.removeEventListener('scroll', this.#handleScroll);
    }
  };

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #handleResize = () => {
    if (this.#resizeRafId) return;

    this.#resizeRafId = requestAnimationFrame(() => {
      this.#resizeRafId = 0;
      this.#measure();
      this.#update();
    });
  };

  /** Reads layout once (forces a synchronous reflow) and caches it, instead
   *  of doing that read on every scroll frame. */
  #measure() {
    const rect = this.getBoundingClientRect();
    this.#heroTop = rect.top + window.scrollY;
    this.#heroHeight = rect.height || 1;
  }

  #update() {
    // No layout read here: just arithmetic against the cached measurements
    // and the (already free) scroll position, so this can run every frame
    // without forcing a reflow.
    const scrolledPast = Math.max(0, window.scrollY - this.#heroTop);
    const progress = clamp(scrolledPast / this.#heroHeight, 0, 1);

    const skyShiftPercent = progress * MAX_SKY_SHIFT_PERCENT;
    const jarShiftPx = -Math.min(MAX_JAR_SHIFT_PX, scrolledPast * JAR_PARALLAX_SPEED);

    this.style.setProperty('--sky-x', `${skyShiftPercent}%`);
    this.style.setProperty('--jar-y', `${jarShiftPx}px`);
  }
}

if (!customElements.get('hero-parallax-media')) {
  customElements.define('hero-parallax-media', HeroParallaxMedia);
}
